"use client";

import { motion } from "framer-motion";
import React, { useState } from "react";
import { featureSections } from "../data";

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const DetailedFeatureSection = () => {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  // Variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.17
      }
    }
  };

  return (
    <div className="w-full max-w-[80%] mx-auto space-y-24 mb-24">
      {featureSections.map((section, idx) => (
        <motion.div
          key={idx}
          className="flex flex-col lg:flex-row gap-8 items-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.2, delay: idx * 0.05 }}
          onMouseEnter={() => setActiveSection(idx)}
          onMouseLeave={() => setActiveSection(null)}
        >
          {/* Left side with icon and competitor gap */}
          <div className="w-full lg:w-2/5 flex flex-col">
            <div className="relative p-6 md:p-8 rounded-xl bg-gradient-to-br from-slate-800/90 to-slate-900/90 border border-slate-700/20 shadow-md transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className={`text-[#38bdf8] p-4 rounded-xl bg-blue-900/20 mr-4 transition-colors duration-100 relative z-10 ${activeSection === idx ? 'scale-105' : 'scale-100'} transition-transform duration-170`}>
                  {React.cloneElement(section.icon, { width: 48, height: 48 })}
                </div>
                <h4 className="text-xl font-semibold text-white">
                  {section.title}
                </h4>
              </div>
              
              <div className="p-4 bg-blue-900/10 border border-blue-500/10 rounded-lg">
                <p className="text-slate-300 leading-relaxed text-sm">
                  <span className="font-semibold text-[#38bdf8]">Competitor Gap:</span> {section.gap}
                </p>
              </div>
            </div>
          </div>
          
          {/* Right side with content */}
          <div className="w-full lg:w-3/5">
            <motion.ul 
              className="space-y-5 mt-0"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {section.features.map((feature, i) => (
                <motion.li 
                  className="flex items-start backdrop-blur-sm rounded-lg bg-slate-800/10 p-4 border border-slate-700/10 hover:border-blue-700/20 transition-colors duration-100" 
                  key={i}
                  variants={itemVariants}
                >
                  <span className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-900/20 border border-blue-500/10 flex items-center justify-center mr-4 mt-1">
                    <CheckIcon />
                  </span>
                  <div>
                    <span className="font-semibold text-white block mb-1 text-lg">{feature.label}</span>
                    <span className="text-slate-300 leading-relaxed">{feature.desc}</span>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default DetailedFeatureSection; 